import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import {
  POSCard,
  POSCardHeader,
  POSCardBody,
  POSDatePill,
  POSButton,
  POSIconButton,
  POSCheckoutCard,
  POSModal,
} from 'components/POS';
import CodeBlock from '../../components/CodeBlock';
import PropsTable from '../../components/PropsTable';

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary
      : '#000033'};
`;

const SectionDescription = styled.p`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary
      : '#64748B'};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const DemoContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.primary ||
        'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
      : '#f8fafc'};
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 16px;
`;

const DemoRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: ${(props) => (props.noMargin ? '0' : '16px')};
`;

const DemoLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.muted
      : '#64748B'};
  min-width: 120px;
`;

const POSStyleGuide = () => {
  const [modalVisible, setModalVisible] = useState(false);

  return (
    <div>
      <h1>POS Components</h1>
      <p style={{ marginBottom: 32 }}>
        A collection of themed components for Point of Sale interfaces. These
        components support both light and dark themes and are designed for
        optimal usability in POS environments.
      </p>

      {/* POSDatePill */}
      <Section>
        <SectionTitle>POSDatePill</SectionTitle>
        <SectionDescription>
          Displays event dates in a compact pill format. Supports single dates
          and date ranges for multi-day events.
        </SectionDescription>

        <ThemeProvider theme={darkTheme}>
          <DemoContainer>
            <DemoRow>
              <DemoLabel>Single Day:</DemoLabel>
              <POSDatePill startDate="02-14-2026" />
            </DemoRow>
            <DemoRow>
              <DemoLabel>Same Month:</DemoLabel>
              <POSDatePill
                startDate="02-14-2026"
                endDate="02-16-2026"
                isMultiDay
              />
            </DemoRow>
            <DemoRow noMargin>
              <DemoLabel>Diff Months:</DemoLabel>
              <POSDatePill
                startDate="02-28-2026"
                endDate="03-02-2026"
                isMultiDay
              />
            </DemoRow>
          </DemoContainer>
        </ThemeProvider>

        <CodeBlock>
          {`import { POSDatePill } from 'components/POS';

// Single day event
<POSDatePill startDate="02-14-2026" />

// Multi-day event (same month)
<POSDatePill
  startDate="02-14-2026"
  endDate="02-16-2026"
  isMultiDay
/>

// Multi-day event (different months)
<POSDatePill
  startDate="02-28-2026"
  endDate="03-02-2026"
  isMultiDay
/>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'startDate',
              type: 'string',
              default: '-',
              description: 'Start date in MM-DD-YYYY format',
            },
            {
              name: 'endDate',
              type: 'string',
              default: '-',
              description: 'End date in MM-DD-YYYY format (optional)',
            },
            {
              name: 'isMultiDay',
              type: 'boolean',
              default: 'false',
              description: 'Whether the event spans multiple days',
            },
          ]}
        />
      </Section>

      {/* POSButton */}
      <Section>
        <SectionTitle>POSButton</SectionTitle>
        <SectionDescription>
          Themed buttons for POS interfaces with multiple variants.
        </SectionDescription>

        <ThemeProvider theme={darkTheme}>
          <DemoContainer>
            <DemoRow>
              <DemoLabel>Primary:</DemoLabel>
              <POSButton variant="primary">Add to Cart</POSButton>
            </DemoRow>
            <DemoRow>
              <DemoLabel>Outlined:</DemoLabel>
              <POSButton variant="outlined">Issue Credit</POSButton>
            </DemoRow>
            <DemoRow>
              <DemoLabel>Secondary:</DemoLabel>
              <POSButton variant="secondary">Cancel</POSButton>
            </DemoRow>
            <DemoRow>
              <DemoLabel>Danger:</DemoLabel>
              <POSButton variant="danger">Clear Cart</POSButton>
            </DemoRow>
            <DemoRow noMargin>
              <DemoLabel>Icon Buttons:</DemoLabel>
              <POSIconButton>+</POSIconButton>
              <POSIconButton variant="ghost">-</POSIconButton>
            </DemoRow>
          </DemoContainer>
        </ThemeProvider>

        <CodeBlock>
          {`import { POSButton, POSIconButton } from 'components/POS';

<POSButton variant="primary">Add to Cart</POSButton>
<POSButton variant="outlined">Issue Credit</POSButton>
<POSButton variant="secondary">Cancel</POSButton>
<POSButton variant="danger">Clear Cart</POSButton>

<POSIconButton>+</POSIconButton>
<POSIconButton variant="ghost">-</POSIconButton>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'variant',
              type: "'primary' | 'outlined' | 'secondary' | 'danger'",
              default: 'primary',
              description: 'Button style variant',
            },
            {
              name: 'size',
              type: "'default' | 'large'",
              default: 'default',
              description: 'Button size',
            },
            {
              name: 'fullWidth',
              type: 'boolean',
              default: 'false',
              description: 'Whether button takes full width',
            },
            {
              name: 'disabled',
              type: 'boolean',
              default: 'false',
              description: 'Disable the button',
            },
          ]}
        />
      </Section>

      {/* POSCard */}
      <Section>
        <SectionTitle>POSCard</SectionTitle>
        <SectionDescription>
          Card container for POS content sections with header and body.
        </SectionDescription>

        <ThemeProvider theme={darkTheme}>
          <DemoContainer>
            <POSCard style={{ maxWidth: 400 }}>
              <POSCardHeader>
                <span style={{ fontWeight: 600, color: '#fff' }}>
                  Cart Summary
                </span>
              </POSCardHeader>
              <POSCardBody>
                <p style={{ color: '#94A3B8', margin: 0 }}>
                  Your cart items will appear here.
                </p>
              </POSCardBody>
            </POSCard>
          </DemoContainer>
        </ThemeProvider>

        <CodeBlock>
          {`import { POSCard, POSCardHeader, POSCardBody } from 'components/POS';

<POSCard>
  <POSCardHeader>
    <span>Cart Summary</span>
  </POSCardHeader>
  <POSCardBody>
    <p>Your cart items will appear here.</p>
  </POSCardBody>
</POSCard>`}
        </CodeBlock>
      </Section>

      {/* POSModal */}
      <Section>
        <SectionTitle>POSModal</SectionTitle>
        <SectionDescription>
          A themed modal component for POS interfaces. Supports dark/light
          themes, responsive design, and customizable header, body, and footer.
        </SectionDescription>

        <ThemeProvider theme={darkTheme}>
          <DemoContainer>
            <DemoRow>
              <POSButton
                variant="primary"
                onClick={() => setModalVisible(true)}
              >
                Open Modal
              </POSButton>
            </DemoRow>
          </DemoContainer>
        </ThemeProvider>

        <ThemeProvider theme={darkTheme}>
          <POSModal
            isVisible={modalVisible}
            onClose={() => setModalVisible(false)}
            title="Example Modal"
          >
            <p style={{ color: '#94A3B8', marginBottom: 16 }}>
              This is an example modal with themed styling for POS interfaces.
            </p>
            <POSButton variant="primary" onClick={() => setModalVisible(false)}>
              Close Modal
            </POSButton>
          </POSModal>
        </ThemeProvider>

        <CodeBlock>
          {`import { POSModal, POSButton } from 'components/POS';

const [isVisible, setIsVisible] = useState(false);

<POSButton onClick={() => setIsVisible(true)}>
  Open Modal
</POSButton>

<POSModal
  isVisible={isVisible}
  onClose={() => setIsVisible(false)}
  title="Payment Confirmation"
>
  <p>Modal content goes here.</p>
  <POSButton onClick={() => setIsVisible(false)}>
    Confirm
  </POSButton>
</POSModal>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'isVisible',
              type: 'boolean',
              default: 'false',
              description: 'Controls modal visibility',
            },
            {
              name: 'onClose',
              type: 'function',
              default: '-',
              description: 'Callback when modal is closed',
            },
            {
              name: 'title',
              type: 'string',
              default: '-',
              description: 'Modal title text',
            },
            {
              name: 'children',
              type: 'ReactNode',
              default: '-',
              description: 'Modal body content',
            },
            {
              name: 'footer',
              type: 'ReactNode',
              default: '-',
              description: 'Optional footer content',
            },
            {
              name: 'noPadding',
              type: 'boolean',
              default: 'false',
              description: 'Remove body padding',
            },
            {
              name: 'noHeader',
              type: 'boolean',
              default: 'false',
              description: 'Hide the modal header',
            },
          ]}
        />
      </Section>

      {/* POSCheckoutCard */}
      <Section>
        <SectionTitle>POSCheckoutCard</SectionTitle>
        <SectionDescription>
          Complete checkout card with summary and payment buttons. The cash
          button uses an outlined orange style.
        </SectionDescription>

        <ThemeProvider theme={darkTheme}>
          <DemoContainer>
            <div style={{ maxWidth: 400 }}>
              <POSCheckoutCard
                subtotal="$40.00"
                fees="$0.00"
                total="$40.00"
                showCard
                showCash
                onCardPayment={() => alert('Card payment')}
                onCashPayment={() => alert('Cash payment')}
              />
            </div>
          </DemoContainer>
        </ThemeProvider>

        <CodeBlock>
          {`import { POSCheckoutCard } from 'components/POS';

<POSCheckoutCard
  subtotal="$40.00"
  fees="$0.00"
  total="$40.00"
  showCard
  showCash
  onCardPayment={() => handleCardPayment()}
  onCashPayment={() => handleCashPayment()}
/>`}
        </CodeBlock>

        <PropsTable
          props={[
            {
              name: 'subtotal',
              type: 'string',
              default: '$0.00',
              description: 'Formatted subtotal string',
            },
            {
              name: 'fees',
              type: 'string',
              default: '$0.00',
              description: 'Formatted fees string',
            },
            {
              name: 'discount',
              type: 'string',
              default: '-',
              description: 'Formatted discount string (optional)',
            },
            {
              name: 'total',
              type: 'string',
              default: '$0.00',
              description: 'Formatted total string',
            },
            {
              name: 'showCard',
              type: 'boolean',
              default: 'true',
              description: 'Show card payment button',
            },
            {
              name: 'showCash',
              type: 'boolean',
              default: 'true',
              description: 'Show cash payment button',
            },
            {
              name: 'onCardPayment',
              type: 'function',
              default: '-',
              description: 'Callback for card payment',
            },
            {
              name: 'onCashPayment',
              type: 'function',
              default: '-',
              description: 'Callback for cash payment',
            },
            {
              name: 'disabled',
              type: 'boolean',
              default: 'false',
              description: 'Disable payment buttons',
            },
          ]}
        />
      </Section>

      {/* Usage Example */}
      <Section>
        <SectionTitle>Full Page Example</SectionTitle>
        <SectionDescription>
          Import the POS components and wrap with the dark theme for POS pages.
        </SectionDescription>

        <CodeBlock>
          {`import { ThemeProvider } from 'styled-components';
import {
  POSCard,
  POSCardHeader,
  POSCardBody,
  POSDatePill,
  POSCheckoutCard,
} from 'components/POS';
import darkTheme from 'themes/darkTheme';

const POSPage = () => (
  <ThemeProvider theme={darkTheme}>
    <div style={{ background: darkTheme.colors.background.primary }}>
      {/* Event Card */}
      <POSCard>
        <POSCardHeader>
          <POSDatePill
            startDate={event.start_date}
            endDate={event.end_date}
            isMultiDay={event.isMultiDay}
          />
          <h1>{event.name}</h1>
        </POSCardHeader>
        <POSCardBody>
          {/* Ticket selection */}
        </POSCardBody>
      </POSCard>

      {/* Checkout */}
      <POSCheckoutCard
        subtotal={subtotal}
        total={total}
        onCardPayment={handleCard}
        onCashPayment={handleCash}
      />
    </div>
  </ThemeProvider>
);`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default POSStyleGuide;
